import {
  DatePlaceholderInParams,
  EntityPlaceholderInParams,
  NumberPlaceholderInParams,
  StringPlaceholderInParams,
} from '@lexian/api/invoicing-api';
import i18n from '_/i18n';

import {
  CreateTemporaryDocumentProps,
  isEntityPlaceholderType,
  LabeledTemplatePlaceholderWizardStep,
  Placeholder,
  PlaceholderPage,
  TemplateWizardStep,
} from './template.types';

export const composeTemplateWizardSteps = (
  pagedPlaceholders: PlaceholderPage[],
  includeRecipientStep = false
): LabeledTemplatePlaceholderWizardStep[] => {
  const wizardSteps: LabeledTemplatePlaceholderWizardStep[] = [
    {
      key: TemplateWizardStep.ChooseTemplate,
      label: i18n.t(
        'communication:createEmailFromTemplateDialog.progressBar.choose'
      ),
    },
  ];

  if (includeRecipientStep) {
    wizardSteps.push({
      key: TemplateWizardStep.Recipient,
      label: i18n.t(
        'communication:createEmailFromTemplateDialog.progressBar.recipients'
      ),
    });
  }

  if (pagedPlaceholders.length > 0) {
    pagedPlaceholders.forEach(({key, label, placeholders}) => {
      if (placeholders.length > 0) {
        wizardSteps.push({key, label});
      }
    });
  }

  wizardSteps.push({
    key: TemplateWizardStep.Preview,
    label: i18n.t(
      'communication:createEmailFromTemplateDialog.progressBar.preview'
    ),
  });

  return wizardSteps;
};

export const flatten = (placeholders: Placeholder[]): Placeholder[] =>
  placeholders.flatMap((p) => [p, ...flatten(p.placeholders ?? [])]);

export const getCreateTemporaryDocumentParams = (
  pagedPlaceholders: PlaceholderPage[]
): CreateTemporaryDocumentProps => {
  const placeholders = pagedPlaceholders.flatMap((p) => [
    ...flatten(p.placeholders ?? []),
  ]);

  const datePlaceholders: DatePlaceholderInParams[] = placeholders.flatMap(
    ({key, type, value}) =>
      type === 'DATE' ? ({key, type, value} as DatePlaceholderInParams) : []
  );

  const entityPlaceholders: EntityPlaceholderInParams[] = placeholders.flatMap(
    ({key, type, value}) =>
      isEntityPlaceholderType(type)
        ? ({
            key,
            type,
            identifier: Array.isArray(value)
              ? value.map((v) => v.value)
              : [value],
          } as EntityPlaceholderInParams)
        : []
  );

  const stringPlaceholders = placeholders
    .filter(
      (placeholder) =>
        !('placeholders' in placeholder) || !placeholder.placeholders
    )
    .flatMap((placeholder) => {
      const {key, type, value, defaultValue, editFieldType} = placeholder;
      if (type === 'ADDRESS' && !editFieldType) {
        const textValue = defaultValue[0]?.options?.find(
          ({entityId}) => entityId === value
        )?.fieldValue;
        return {
          key,
          type,
          identifier: value,
          value: textValue ? [textValue] : [],
        } as StringPlaceholderInParams;
      }

      return type === 'TEXT' ||
        type === 'TEXTAREA' ||
        type === 'USER_AND_ROLE' ||
        type === 'USER_AND_CONTACT_ROLE' ||
        type === 'BILLABLE_CATEGORY' ||
        type === 'LOCALE' ||
        type === 'ENUM' ||
        type === 'EMAIL' ||
        type === 'PHONE' ||
        type === 'ADDRESS' ||
        type === 'LINK'
        ? ({
            key,
            type,
            identifier: getStringPlaceholderIdentifier(placeholder),
            value: getStringPlaceholderValue(placeholder),
          } as StringPlaceholderInParams)
        : [];
    });

  const numberPlaceholders: NumberPlaceholderInParams[] = placeholders.flatMap(
    ({key, type, value}) =>
      type === 'NUMBER' ? ({key, type, value} as NumberPlaceholderInParams) : []
  );

  return {
    datePlaceholders,
    entityPlaceholders,
    stringPlaceholders,
    numberPlaceholders,
  };
};

const getStringPlaceholderIdentifier = ({
  doNotAutoUpdate,
  type,
  optional,
  value,
  identifier,
  defaultValue,
}: Placeholder) => {
  if (type === 'ADDRESS' && doNotAutoUpdate) {
    return undefined;
  }

  if (
    optional &&
    (Array.isArray(value) ? value.length === 0 : !value || value.length === 0)
  ) {
    return undefined;
  }

  return identifier ?? defaultValue[0]?.entityId;
};

const getStringPlaceholderValue = ({value, optional}: Placeholder) => {
  if (optional && !value) {
    return undefined;
  }

  return Array.isArray(value) ? value.map((v) => v.value) : [value];
};
